import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
  Table,
  Input,
  Select,
  Button,
  Tag,
  Breadcrumb,
  Spin,
  message,
  Space,
  Popconfirm,
  Checkbox,
  Modal,
  Divider,
} from "antd";
import {
  SearchOutlined,
  FilterOutlined,
  PlusOutlined,
  DeleteOutlined,
  HistoryOutlined,
} from "@ant-design/icons";
import { CALL_API } from "common/API";
import { STATUS_CODE } from "common/Constants";
import AddAssetModal from "./AddAssetModal";
import AssetDetailDrawer from "./AssetDetailDrawer";
import AssetHistoryModal from "./AssetHistoryModal";
import AssignAssetModal from "./AssignAssetModal";
import { useDispatch, useSelector } from "react-redux";
import {
  getClientid,
  getMenuList,
  getUserInfo,
  getUserList,
  getUserRoleId,
} from "redux/selectors";
import dayjs from "dayjs";
import { ButtonFilled, ButtonOutlined } from "reusableComponent/UIButtons";
import { setAssetsList } from "redux/actions";

const { Option } = Select;

/* ─── Styled ─────────────────────────────────────────────── */
const PageWrapper = styled.div`
  position: relative;
  height: calc(100vh - 150px);

  .scroll-container {
    overflow: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;
  }

  .scroll-container::-webkit-scrollbar {
    display: none;
  }
`;

const Toolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const ToolbarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const ToolbarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const AddAssetBtn = styled.span`
  color: #2563eb;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 4px;
  &:hover {
    text-decoration: underline;
  }
`;

const BulkRevokeBtn = styled.span`
  color: #2563eb;
  cursor: pointer;
  font-size: 14px;
  &:hover {
    text-decoration: underline;
  }
`;

const AssetLink = styled.span`
  color: #2563eb;
  cursor: pointer;
  font-weight: 500;
  &:hover {
    text-decoration: underline;
  }
`;

const InServiceTag = styled(Tag)`
  background: #fff3e0;
  color: #e65100;
  border-color: #ffb74d;
  border-radius: 12px;
  padding: 2px 10px;
  font-size: 12px;
`;

const HoverActions = styled.div`
  display: flex;
  gap: 6px;
  flex-shrink: 0;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  pointer-events: ${({ $visible }) => ($visible ? "auto" : "none")};
  transition: opacity 0.15s ease;
`;

const CategoryDetailPage = ({ category, onBack }) => {
  const userList = useSelector(getUserList);
  const userInfo = useSelector(getUserInfo);
  const clientId = useSelector(getClientid);
  const menuList = useSelector(getMenuList);
  const userRoleId = useSelector(getUserRoleId);

  const Menulist =
    menuList[userRoleId] || Object.values(menuList)[0] || menuList;
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [modelFilter, setModelFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [assignedFilter, setAssignedFilter] = useState(null);
  const [addAssetOpen, setAddAssetOpen] = useState(false);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [hoveredKey, setHoveredKey] = useState(null);
  const [revokeLoading, setRevokeLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [bulkRevokeLoading, setBulkRevokeLoading] = useState(false);
  const [editAssetOpen, setEditAssetOpen] = useState(false);
  const [editAsset, setEditAsset] = useState(null);
  const [showBulkRevoke, setShowBulkRevoke] = useState(false);

  const dispatch = useDispatch();

  const footerStyle = {
    position: "sticky",
    bottom: -20,
    left: 0,
    right: 0,
    backgroundColor: "#fff",
    padding: "10px",
    display: "flex",
    justifyContent: "end",
    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
    margin: "0px -20px",
    zIndex: 10,
  };

  const fetchMasterData = async (fetch_master_data) => {
    const { code, assetsCategories } = await CALL_API(
      `assets/getassets/all`,
      "get"
    );
    if (code === STATUS_CODE.SUCCESS) {
      dispatch(setAssetsList(assetsCategories));
    }
  };

  const handleBulkRevoke = async () => {
    if (!selectedRowKeys.length) {
      message.warning("Please select at least one asset");
      return;
    }

    try {
      setBulkRevokeLoading(true);

      // collect assigned_to for the selected assets (unique values)
      const assignedTos = selectedRowKeys
        .map((id) => assets.find((a) => a.assetid === id)?.assigned_to)
        .filter(Boolean);
      const uniqueAssignedTos = [...new Set(assignedTos)];

      const { code } = await CALL_API(
        "/assets/bulk-revoke/bulk-assets",
        "patch",
        {
          assetids: selectedRowKeys,
          assigned_to: uniqueAssignedTos,
          revoked_by: userInfo?.userid,
          revoked_on: new Date(),
        }
      );

      if (code === STATUS_CODE.SUCCESS) {
        setSelectedRowKeys([]);
        getAssets();
        await fetchMasterData(true);
        setSelectedRowKeys([]);
        setShowBulkRevoke(false);
        message.success("Assets revoked successfully");
      } else {
        message.error("Failed to revoke assets");
      }
    } catch (error) {
      console.error(error);
      message.error("Failed to revoke assets");
    } finally {
      setBulkRevokeLoading(false);
    }
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => {
      setSelectedRowKeys(keys);
    },
    getCheckboxProps: (record) => ({
      disabled: !record.assigned_to,
    }),
  };

  const handleAssignAsset = (record) => {
    setSelectedAsset(record);
    setAssignModalOpen(true);
  };

  const handleHistory = (record) => {
    getAssetsHistory(record.assetid);
    setSelectedAsset(record);
    setHistoryModalOpen(true);
  };

  const getAssetsHistory = async (id) => {
    try {
      setHistoryLoading(true);
      const { historyList, code } = await CALL_API(
        `assets/assets-history/${id}`,
        "get"
      );
      if (code === STATUS_CODE.SUCCESS) {
        setHistoryData(Array.isArray(historyList) ? historyList : []);
      } else {
        throw new Error(`Failed: ${code}`);
      }
    } catch (error) {
      message.error("Failed to load History");
      console.error(error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const getAssets = async () => {
    try {
      setLoading(true);
      const { assetList, code } = await CALL_API(
        `/assets/getassets/${category?.categoryid}`,
        "get"
      );
      if (code === STATUS_CODE.SUCCESS) {
        setAssets(Array.isArray(assetList) ? assetList : []);
      } else {
        throw new Error(`Failed: ${code}`);
      }
    } catch (error) {
      message.error("Failed to load assets");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (category?.categoryid) getAssets();
  }, [category]);

  const filtered = assets.filter((a) => {
    if (
      search &&
      !String(a?.assetname || "")
        .toLowerCase()
        .includes(search.toLowerCase())
    ) {
      return false;
    }

    if (modelFilter && a?.model !== modelFilter) {
      return false;
    }

    if (statusFilter) {
      const currentStatus =
        a?.asset_service_status === "In Service"
          ? "In Service"
          : a?.assetstatus;

      if (currentStatus !== statusFilter) {
        return false;
      }
    }

    if (assignedFilter && a?.assigned_to !== assignedFilter) {
      return false;
    }

    return true;
  });

  const uniqueModels = [...new Set(assets.map((a) => a.model).filter(Boolean))];
  const uniqueAssigned = [
    ...new Set(assets.map((a) => a.assigned_to).filter(Boolean)),
  ];

  const handleAssetClick = (record) => {
    setSelectedAsset(record);
    setDrawerOpen(true);
  };

  const handleRevokeAsset = async (record) => {
    try {
      setRevokeLoading(true);

      const { code } = await CALL_API(
        `/assets/revokeassets/${record.assetid}`,
        "patch",
        {
          revoked_by: userInfo?.userid,
          revoked_on: new Date(),
          assetstatus: "Available",
          assigned_to: record?.assigned_to,
        }
      );

      if (code === STATUS_CODE.SUCCESS) {
        await fetchMasterData(true);
        message.success("Asset revoked successfully");
        getAssets();
      } else {
        message.error("Failed to revoke asset");
      }
    } catch (error) {
      console.error(error);
      message.error("Failed to revoke asset");
    } finally {
      setRevokeLoading(false);
    }
  };

  const handleDeleteAsset = async (record) => {
    try {
      setDeleteLoading(true);

      const { code } = await CALL_API(
        `/assets/deleteassets/${record.assetid}`,
        "delete"
      );

      if (code === STATUS_CODE.SUCCESS) {
        await fetchMasterData(true);
        message.success("Asset Removed successfully");
        getAssets();
      } else {
        message.error("Failed to remove asset");
      }
    } catch (error) {
      console.error(error);
      message.error("Failed to remove asset");
    } finally {
      setDeleteLoading(false);
    }
  };

  const confirmRevokeAsset = (record) => {
    const modal = Modal.confirm({
      title: "Revoke Asset",
      content: `Are you sure you want to revoke ${record.assetname}?`,
      okText: "Revoke",
      cancelText: "Cancel",
      centered: true,
      onOk: async () => {
        modal.update({
          okButtonProps: { loading: true },
        });

        try {
          await handleRevokeAsset(record);
          modal.destroy();
        } catch (error) {
          modal.update({
            okButtonProps: { loading: false },
          });
        }
      },
    });
  };

  const confirmDeleteAsset = (record) => {
    const modal = Modal.confirm({
      title: "Delete Asset",
      content: `Are you sure you want to delete ${record.assetname}?`,
      okText: "Delete",
      cancelText: "Cancel",
      centered: true,
      onOk: async () => {
        modal.update({
          okButtonProps: {
            loading: true,
          },
        });

        try {
          await handleDeleteAsset(record);
          modal.destroy();
        } catch (error) {
          modal.update({
            okButtonProps: {
              loading: false,
            },
          });
        }
      },
    });
  };

  const confirmBulkRevoke = () => {
    Modal.confirm({
      title: "Bulk Revoke Assets",
      content: `Are you sure you want to revoke selected assets?`,
      okText: "Revoke",
      cancelText: "Cancel",
      centered: true,
      onOk: () => handleBulkRevoke(),
    });
  };

  const columns = [
    {
      title: "Asset Id",
      key: "assetid",
      dataIndex: "assetid",
      render: (id) => {
        return `ASS${id}`;
      },
    },
    {
      title: "Asset Name",
      key: "assetname",
      render: (_, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <AssetLink onClick={() => handleAssetClick(record)}>
            {record.assetname}
          </AssetLink>
          {record.asset_service_status === "In Service" && (
            <InServiceTag>In Service</InServiceTag>
          )}
        </div>
      ),
    },
    {
      title: "Model",
      dataIndex: "model",
      key: "model",
      render: (v) => v || <span style={{ color: "#bbb" }}>-</span>,
    },
    {
      title: "No. of Services",
      dataIndex: "noofservices",
      key: "noofservices",
      render: (v) => v ?? 0,
    },
    {
      title: "Assigned to",
      key: "assigned_to",
      render: (_, record) =>
        record.assigned_to ? (
          <div>
            <div style={{ fontWeight: 500 }}>
              {userList?.find((u) => u.userid === record.assigned_to)?.fullname}
            </div>
            <div style={{ color: "#888", fontSize: 12, fontStyle: "italic" }}>
              {record.employeeid}
            </div>
          </div>
        ) : (
          <span style={{ color: "#bbb" }}>-</span>
        ),
    },
    {
      title: "Assigned on",
      dataIndex: "assigned_on",
      key: "assigned_on",
      render: (v) =>
        dayjs(v).isValid() ? (
          dayjs(v).format("Do MMM YYYY")
        ) : (
          <span style={{ color: "#bbb" }}>-</span>
        ),
    },

    {
      title: "",
      dataIndex: "assigned_to",
      key: "assigned_to",
      render: (v, record) => (
        <HoverActions
          $visible={
            hoveredKey === record.assetid && record.categorystatus !== "DEFAULT"
          }
        >
          <Space size={20}>
            {Menulist?.find(
              (item) =>
                item?.key === "HRResources" &&
                item?.subMenu?.find(
                  (subItem) =>
                    subItem?.key === "assets" && subItem?.permission?.edit
                )
            ) && (
              <>
                {record?.assigned_to ? (
                  <Button
                    type="link"
                    onClick={() => confirmRevokeAsset(record)}
                    style={{
                      padding: 0,
                      color: "#2D53DA",
                    }}
                  >
                    Revoke Asset
                  </Button>
                ) : (
                  <Button
                    type="link"
                    onClick={() => handleAssignAsset(record)}
                    style={{
                      padding: 0,
                      color: "#2D53DA",
                    }}
                  >
                    Assign Asset
                  </Button>
                )}
              </>
            )}

            <HistoryOutlined
              onClick={() => handleHistory(record)}
              style={{
                color: "#2D53DA",
                cursor: "pointer",
                fontSize: 16,
              }}
            />
            {!record?.assigned_to &&
              Menulist?.find(
                (item) =>
                  item?.key === "HRResources" &&
                  item?.subMenu?.find(
                    (subItem) =>
                      subItem?.key === "assets" && subItem?.permission?.delete
                  )
              ) && (
                <DeleteOutlined
                  onClick={() => confirmDeleteAsset(record)}
                  style={{
                    color: "#2D53DA",
                    cursor: "pointer",
                    fontSize: 16,
                  }}
                />
              )}
          </Space>
        </HoverActions>
      ),
    },
    ...(showBulkRevoke
      ? [
          {
            title: (
              <Checkbox
                checked={
                  filtered.filter((a) => a.assigned_to).length > 0 &&
                  selectedRowKeys.length ===
                    filtered.filter((a) => a.assigned_to).length
                }
                indeterminate={
                  selectedRowKeys.length > 0 &&
                  selectedRowKeys.length <
                    filtered.filter((a) => a.assigned_to).length
                }
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedRowKeys(
                      filtered
                        .filter((a) => a.assigned_to)
                        .map((a) => a.assetid)
                    );
                  } else {
                    setSelectedRowKeys([]);
                  }
                }}
              />
            ),
            key: "selection",
            width: 70,
            align: "center",
            render: (_, record) => (
              <Checkbox
                disabled={!record.assigned_to}
                checked={selectedRowKeys.includes(record.assetid)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedRowKeys((prev) => [...prev, record.assetid]);
                  } else {
                    setSelectedRowKeys((prev) =>
                      prev.filter((id) => id !== record.assetid)
                    );
                  }
                }}
              />
            ),
          },
        ]
      : []),
  ];

  return (
    <PageWrapper>
      <div
        className="scroll-container"
        style={{
          height: selectedRowKeys?.length ? "calc(100vh - 195px)" : "",
          overflowY: "auto",
        }}
      >
        {/* ── Breadcrumb ── */}
        <Breadcrumb style={{ marginBottom: 20 }}>
          <Breadcrumb.Item>
            <span
              style={{ cursor: "pointer", color: "#2563eb" }}
              onClick={onBack}
            >
              Asset
            </span>
          </Breadcrumb.Item>
          <Breadcrumb.Item>{category?.categoryname}</Breadcrumb.Item>
        </Breadcrumb>

        {/* ── Toolbar ── */}
        <Toolbar>
          <ToolbarLeft>
            <span style={{ fontWeight: 500, fontSize: 14 }}>
              Total Assets: <strong>{assets.length}</strong>
            </span>
            <Input
              prefix={<SearchOutlined />}
              placeholder="Asset Name"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: 200, borderRadius: 20 }}
              allowClear
            />
            <Select
              placeholder="Model"
              style={{ width: 130 }}
              allowClear
              onChange={setModelFilter}
              suffixIcon={<FilterOutlined />}
            >
              {uniqueModels.map((m) => (
                <Option key={m} value={m}>
                  {m}
                </Option>
              ))}
            </Select>
            <Select
              placeholder="Status"
              style={{ width: 130 }}
              allowClear
              onChange={setStatusFilter}
              suffixIcon={<FilterOutlined />}
            >
              <Option value="In Service">In Service</Option>
              <Option value="Assigned">Assigned</Option>
              <Option value="Unassigned">Unassigned</Option>
            </Select>
            <Select
              placeholder="Assigned to"
              style={{ width: 150 }}
              allowClear
              onChange={setAssignedFilter}
              suffixIcon={<FilterOutlined />}
            >
              {userList
                ?.filter((item) => uniqueAssigned?.includes(item?.userid))
                .map((a) => (
                  <Option key={a?.userid} value={a?.userid}>
                    {a?.fullname}
                  </Option>
                ))}
            </Select>
          </ToolbarLeft>

          <ToolbarRight>
            {Menulist?.find(
              (item) =>
                item?.key === "HRResources" &&
                item?.subMenu?.find(
                  (subItem) =>
                    subItem?.key === "assets" && subItem?.permission?.create
                )
            ) && (
              <>
                <AddAssetBtn onClick={() => setAddAssetOpen(true)}>
                  <PlusOutlined /> Add Asset
                </AddAssetBtn>
                <Divider type="vertical" />
              </>
            )}
            {Menulist?.find(
              (item) =>
                item?.key === "HRResources" &&
                item?.subMenu?.find(
                  (subItem) =>
                    subItem?.key === "assets" && subItem?.permission?.delete
                )
            ) && (
              <BulkRevokeBtn onClick={() => setShowBulkRevoke(true)}>
                Bulk Revoke
              </BulkRevokeBtn>
            )}
          </ToolbarRight>
        </Toolbar>

        {/* ── Asset Table ── */}
        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={filtered}
            pagination={false}
            size="middle"
            rowKey="assetid"
            style={{ border: "1px solid #f0f0f0", borderRadius: 8 }}
            onRow={(record) => ({
              onMouseEnter: () => setHoveredKey(record?.assetid),
              onMouseLeave: () => setHoveredKey(null),
              style: {
                background: hoveredKey === record?.assetid ? "#fff" : "#f0f5ff",
                transition: "background 0.15s",
              },
            })}
          />
        </Spin>

        {/* ── Add Asset Modal ── */}
        <AddAssetModal
          open={addAssetOpen}
          onClose={() => setAddAssetOpen(false)}
          categoryList={[category]}
          preselectedCategory={category}
          onSuccess={getAssets}
        />

        {/* ── Asset Detail Drawer ── */}
        <AssetDetailDrawer
          open={drawerOpen}
          onClose={() => {
            setDrawerOpen(false);
            setSelectedAsset(null);
          }}
          asset={selectedAsset}
          onSuccess={getAssets}
          onEdit={(record) => {
            setDrawerOpen(false);
            setEditAsset(record);
            setEditAssetOpen(true);
          }}
          getAssets={getAssets}
          fetchMasterData={fetchMasterData}
        />
        <AssignAssetModal
          open={assignModalOpen}
          onClose={() => setAssignModalOpen(false)}
          asset={selectedAsset}
          getAssets={getAssets}
        />

        <AddAssetModal
          open={editAssetOpen}
          onClose={() => {
            setEditAssetOpen(false);
            setEditAsset(null);
          }}
          categoryList={[category]}
          preselectedCategory={category}
          editData={editAsset}
          onSuccess={getAssets}
        />

        <AssetHistoryModal
          open={historyModalOpen}
          onClose={() => setHistoryModalOpen(false)}
          historyData={historyData}
          loading={historyLoading}
        />
      </div>
      {selectedRowKeys?.length > 0 && (
        <div style={footerStyle}>
          <ButtonOutlined
            style={{ borderRadius: "5px" }}
            onClick={() => {
              setSelectedRowKeys([]);
              setShowBulkRevoke(false);
            }}
          >
            Cancel
          </ButtonOutlined>
          <ButtonFilled
            style={{ borderRadius: "5px" }}
            type="primary"
            // loading={bulkRevokeLoading}
            onClick={confirmBulkRevoke}
          >
            Revoke
          </ButtonFilled>
        </div>
      )}
    </PageWrapper>
  );
};

export default CategoryDetailPage;
